import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  GridFilterInputValueProps,
  GridFilterOperator,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import dvdlocator from "../../../../apis/dvdlocator";
import { renderDate } from "../../../../formatting/dateFormat";
import { SUPPLIERS_BY_KEY } from "../suppliers";
import { classifyFreshness, Freshness } from "./SupplierRollupCell";
import {
  FreshnessDot as Dot,
  InventoryGridLink,
  ProvenanceCell,
  ProvenanceDialog,
  ProvenanceFrame,
  inventoryGridUrlForUpcs,
  money,
  queueProductRecalc,
  useProvenanceDetail,
} from "./MetricProvenance";

export { inventoryGridUrlForUpcs, queueProductRecalc };

/**
 * MRCOG: the product's replacement cost, rolled up into one cell.
 *   IS  in stock with a distributor: cheapest complete combination using only
 *       Alliance-family or DealsAreUs prices with quantity
 *   P   purchasable from any supplier data: the catch-all, dated or not
 * Each value carries a freshness dot for the oldest supplier observation among
 * the parts that produced it. Clicking opens the detail: the winning
 * combination, every part's supplier price with its observation and source,
 * drift against the live price, a link to the parts in the Inventory grid and
 * a "Recalculate" action.
 */

export const MRCOG_KIND_LABELS = {
  IS: "IS: in stock with a distributor",
  P: "P: purchasable from any supplier data",
} as const;

export const MRCOG_DESCRIPTION =
  "Master replacement cost of goods. IS = in stock with a distributor (Alliance-family or DealsAreUs with quantity). P = purchasable from any supplier data, the catch-all. The dot shows how fresh the oldest supplier price behind each value is; click for the parts that produced it.";

export interface MrcogRowValues {
  is: number | null;
  p: number | null;
  isObservedAt: string | null;
  pObservedAt: string | null;
  isUpdatedAt: string | number | null;
  pUpdatedAt: string | number | null;
  isUndated: number | null;
  pUndated: number | null;
  isCombination: number | null;
  pCombination: number | null;
}

export function getMrcogValues(row: any): MrcogRowValues {
  const p = row?.product ?? {};
  return {
    is: p.replacement_cost_dist ?? null,
    p: p.replacement_cost ?? null,
    isObservedAt: p.rcog_dist_observed_at ?? null,
    pObservedAt: p.rcog_observed_at ?? null,
    isUpdatedAt: p.rcog_dist_updated_at ?? null,
    pUpdatedAt: p.rcog_updated_at ?? null,
    isUndated: p.rcog_dist_undated_parts ?? null,
    pUndated: p.rcog_undated_parts ?? null,
    isCombination: p.rcog_dist_combination_id ?? null,
    pCombination: p.rcog_combination_id ?? null,
  };
}

/** Freshness of one MRCOG value: undated parts make it "never" regardless. */
export function mrcogFreshness(
  observedAt: string | null,
  undated: number | null,
  now?: Date
): Freshness {
  if (undated && undated > 0) return "never";
  return classifyFreshness(observedAt, now);
}

function freshnessLine(kind: "IS" | "P", v: MrcogRowValues): string {
  const observed = kind === "IS" ? v.isObservedAt : v.pObservedAt;
  const undated = kind === "IS" ? v.isUndated : v.pUndated;
  const updated = kind === "IS" ? v.isUpdatedAt : v.pUpdatedAt;
  const value = kind === "IS" ? v.is : v.p;
  if (value === null) return `${MRCOG_KIND_LABELS[kind]}: none`;
  const data =
    undated && undated > 0
      ? `${undated} part${undated === 1 ? "" : "s"} undated`
      : observed
      ? `data observed ${renderDate(observed)}`
      : "data undated";
  return `${MRCOG_KIND_LABELS[kind]}: ${money(value)} (${data}; calculated ${
    updated ? renderDate(updated) : "never"
  })`;
}

export function formatMrcogTooltip(v: MrcogRowValues): string {
  return [
    freshnessLine("IS", v),
    freshnessLine("P", v),
    "Click for the parts behind each value.",
  ].join("\n");
}

export const renderMrcogCell = (params: GridRenderCellParams) => {
  const v = getMrcogValues(params.row);
  if (v.is === null && v.p === null) return null;
  return <MrcogCellContent row={params.row} values={v} />;
};

function MrcogCellContent({
  row,
  values: v,
}: {
  row: any;
  values: MrcogRowValues;
}) {
  return (
    <ProvenanceCell
      tooltip={formatMrcogTooltip(v)}
      renderDetail={(close) => (
        <MrcogDetail
          productId={row.product?.product_id}
          productTitle={row.product?.product_title}
          onClose={close}
        />
      )}
    >
      <span>
        <Dot freshness={mrcogFreshness(v.isObservedAt, v.isUndated)} />
        <span style={{ color: "rgba(0,0,0,0.55)", fontSize: "0.8em" }}>
          IS{" "}
        </span>
        {money(v.is)}
      </span>
      <span>
        <Dot freshness={mrcogFreshness(v.pObservedAt, v.pUndated)} />
        <span style={{ color: "rgba(0,0,0,0.55)", fontSize: "0.8em" }}>P </span>
        {money(v.p)}
      </span>
    </ProvenanceCell>
  );
}

// ─── Detail (shared by the cell popover and the context-menu dialog) ─────────

export interface ReplacementCostPart {
  part_number: string;
  inventory_id: number | null;
  upc: string | null;
  supplier_key: string | null;
  list_price: number | null;
  discounted_price: number | null;
  price_observed_at: string | null;
  price_source: string | null;
  current_price: number | null;
  current_observed_at: string | null;
  inventory_title: string | null;
}

export interface ReplacementCostKind {
  cost: number | null;
  calculated_at: string | null;
  observed_at: string | null;
  combination_id: number | null;
  undated_parts: number | null;
  parts: ReplacementCostPart[];
}

export interface ReplacementCostDetail {
  product_id: number;
  P: ReplacementCostKind;
  IS: ReplacementCostKind;
}

export async function fetchReplacementCostDetail(
  productId: number
): Promise<ReplacementCostDetail> {
  const response = await dvdlocator.get(
    `/product/${productId}/replacement-cost`
  );
  return response.data;
}

const priceDrifted = (part: ReplacementCostPart) =>
  part.current_price !== null &&
  part.list_price !== null &&
  Math.abs(Number(part.current_price) - Number(part.list_price)) >= 0.005;

function KindSection({
  kind,
  data,
}: {
  kind: "IS" | "P";
  data: ReplacementCostKind;
}) {
  const upcs = data.parts.map((p) => p.upc).filter((u): u is string => !!u);
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <Dot freshness={mrcogFreshness(data.observed_at, data.undated_parts)} />
        {MRCOG_KIND_LABELS[kind]}: {money(data.cost)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {data.cost === null
          ? "No complete combination could be priced."
          : `Combination #${data.combination_id ?? "?"}, calculated ${
              data.calculated_at ? renderDate(data.calculated_at) : "never"
            }; oldest price observed ${
              data.observed_at ? renderDate(data.observed_at) : "never"
            }${
              data.undated_parts
                ? `; ${data.undated_parts} part(s) undated`
                : ""
            }.`}
      </Typography>
      {data.parts.length > 0 && (
        <Table size="small" sx={{ mt: 0.5 }}>
          <TableHead>
            <TableRow>
              <TableCell>Part</TableCell>
              <TableCell>UPC</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell align="right">Supplier price then</TableCell>
              <TableCell align="right">Now</TableCell>
              <TableCell align="right">Cost used</TableCell>
              <TableCell>Observed</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.parts.map((part) => {
              const supplier = part.supplier_key
                ? SUPPLIERS_BY_KEY[part.supplier_key]
                : undefined;
              const drifted = priceDrifted(part);
              return (
                <TableRow key={`${kind}-${part.part_number}`}>
                  <TableCell title={part.inventory_title ?? undefined}>
                    {part.part_number}
                  </TableCell>
                  <TableCell>{part.upc ?? "—"}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {supplier && (
                        <img
                          src={supplier.icon}
                          alt=""
                          width={14}
                          height={14}
                        />
                      )}
                      {supplier?.name ?? part.supplier_key ?? "—"}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{money(part.list_price)}</TableCell>
                  <TableCell
                    align="right"
                    style={
                      drifted
                        ? { color: "#CC0000", fontWeight: 700 }
                        : undefined
                    }
                  >
                    {part.current_price === null
                      ? "gone"
                      : drifted
                      ? money(part.current_price)
                      : "same"}
                  </TableCell>
                  <TableCell
                    align="right"
                    title="DTB cost: rebates, shipping and the Alliance discount applied"
                  >
                    {money(part.discounted_price)}
                  </TableCell>
                  <TableCell>
                    <Dot
                      freshness={classifyFreshness(part.price_observed_at)}
                    />
                    {part.price_observed_at
                      ? renderDate(part.price_observed_at)
                      : "undated"}
                  </TableCell>
                  <TableCell>{part.price_source ?? "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <InventoryGridLink
        href={inventoryGridUrlForUpcs(upcs)}
        count={upcs.length}
      />
    </Box>
  );
}

export function MrcogDetail({
  productId,
  productTitle,
  onClose,
}: {
  productId: number;
  productTitle?: string;
  onClose?: () => void;
}) {
  const { detail, error } = useProvenanceDetail(
    productId,
    fetchReplacementCostDetail,
    "replacement cost detail"
  );
  return (
    <ProvenanceFrame
      title="Replacement cost"
      productTitle={productTitle}
      productId={productId}
      error={error}
      loaded={detail !== null}
      recalcLabel="Recalculate MRCOG"
      onClose={onClose}
    >
      {detail && (
        <>
          <KindSection kind="IS" data={detail.IS} />
          <KindSection kind="P" data={detail.P} />
        </>
      )}
    </ProvenanceFrame>
  );
}

/** Dialog wrapper for the context menus. */
export function MrcogDetailDialog({
  open,
  onClose,
  product,
}: {
  open: boolean;
  onClose: () => void;
  product: any;
}) {
  return (
    <ProvenanceDialog
      open={open}
      onClose={onClose}
      title="Replacement cost (MRCOG)"
    >
      <MrcogDetail
        productId={product?.product_id}
        productTitle={product?.product_title}
      />
    </ProvenanceDialog>
  );
}

// ─── Filtering ────────────────────────────────────────────────────────────────

export interface MrcogFilterValue {
  is?: { operator: string; value?: string | number };
  p?: { operator: string; value?: string | number };
  isObservedAt?: {
    operator: "within" | "olderThan" | "never" | "ever";
    days?: number;
  };
  pObservedAt?: {
    operator: "within" | "olderThan" | "never" | "ever";
    days?: number;
  };
  isUpdatedAt?: {
    operator: "within" | "olderThan" | "never" | "ever";
    days?: number;
  };
}

const NUMBER_OPERATORS = [
  { value: ">", label: ">" },
  { value: ">=", label: "≥" },
  { value: "<", label: "<" },
  { value: "<=", label: "≤" },
  { value: "=", label: "=" },
  { value: "!=", label: "≠" },
  { value: "isEmpty", label: "is empty" },
  { value: "isNotEmpty", label: "is not empty" },
];
const VALUELESS = new Set(["isEmpty", "isNotEmpty"]);

export function isMrcogFilterComplete(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const v = value as MrcogFilterValue;
  const numberOk = (c?: { operator: string; value?: unknown }) =>
    !!c?.operator &&
    (VALUELESS.has(c.operator) || (c.value !== undefined && c.value !== ""));
  const dateOk = (c?: { operator: string; days?: number }) =>
    !!c?.operator &&
    (c.operator === "never" ||
      c.operator === "ever" ||
      Number.isFinite(Number(c.days)));
  return (
    numberOk(v.is) ||
    numberOk(v.p) ||
    dateOk(v.isObservedAt) ||
    dateOk(v.pObservedAt) ||
    dateOk(v.isUpdatedAt)
  );
}

function NumberCondition({
  label,
  condition,
  onChange,
}: {
  label: string;
  condition?: { operator: string; value?: string | number };
  onChange: (c?: { operator: string; value?: string | number }) => void;
}) {
  const operator = condition?.operator ?? "";
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={operator}
          onChange={(e) => {
            const op = e.target.value;
            onChange(
              op ? { operator: op, value: condition?.value } : undefined
            );
          }}
        >
          <MenuItem value="">any</MenuItem>
          {NUMBER_OPERATORS.map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {operator && !VALUELESS.has(operator) && (
        <TextField
          size="small"
          type="number"
          value={condition?.value ?? ""}
          onChange={(e) =>
            onChange({
              operator,
              value: e.target.value === "" ? undefined : e.target.value,
            })
          }
          sx={{ width: 110 }}
        />
      )}
    </Box>
  );
}

function DateCondition({
  label,
  condition,
  onChange,
}: {
  label: string;
  condition?: {
    operator: "within" | "olderThan" | "never" | "ever";
    days?: number;
  };
  onChange: (c?: {
    operator: "within" | "olderThan" | "never" | "ever";
    days?: number;
  }) => void;
}) {
  const op = condition?.operator ?? "";
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={op}
          onChange={(e) => {
            const next = e.target.value as typeof op;
            onChange(
              next
                ? { operator: next as any, days: condition?.days ?? 7 }
                : undefined
            );
          }}
        >
          <MenuItem value="">any time</MenuItem>
          <MenuItem value="within">within last</MenuItem>
          <MenuItem value="olderThan">older than</MenuItem>
          <MenuItem value="ever">dated</MenuItem>
          <MenuItem value="never">undated</MenuItem>
        </Select>
      </FormControl>
      {(op === "within" || op === "olderThan") && (
        <TextField
          size="small"
          type="number"
          label="days"
          value={condition?.days ?? ""}
          onChange={(e) =>
            onChange({
              operator: op,
              days: e.target.value === "" ? undefined : Number(e.target.value),
            })
          }
          slotProps={{ htmlInput: { min: 0 } }}
          sx={{ width: 90 }}
        />
      )}
    </Box>
  );
}

export interface MrcogFilterFieldsProps {
  value: MrcogFilterValue | undefined;
  onChange: (value: MrcogFilterValue) => void;
}

export function MrcogFilterFields({ value, onChange }: MrcogFilterFieldsProps) {
  const v: MrcogFilterValue = value ?? {};
  const set = (patch: Partial<MrcogFilterValue>) =>
    onChange({ ...v, ...patch });
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 0.5,
        minWidth: 280,
      }}
    >
      <NumberCondition
        label="MRCOG-IS"
        condition={v.is}
        onChange={(c) => set({ is: c })}
      />
      <NumberCondition
        label="MRCOG-P"
        condition={v.p}
        onChange={(c) => set({ p: c })}
      />
      <DateCondition
        label="IS data observed"
        condition={v.isObservedAt}
        onChange={(c) => set({ isObservedAt: c })}
      />
      <DateCondition
        label="P data observed"
        condition={v.pObservedAt}
        onChange={(c) => set({ pObservedAt: c })}
      />
      <DateCondition
        label="IS calculated"
        condition={v.isUpdatedAt}
        onChange={(c) => set({ isUpdatedAt: c })}
      />
      <Typography variant="caption" color="text.secondary">
        IS = in stock with a distributor; P = any supplier data. "Undated"
        matches values whose parts carry no observation date.
      </Typography>
    </Box>
  );
}

function MrcogFilterInput({ item, applyValue }: GridFilterInputValueProps) {
  const [draft, setDraft] = useState<MrcogFilterValue>(item.value ?? {});
  useEffect(() => {
    setDraft(item.value ?? {});
  }, [item.value]);
  return (
    <MrcogFilterFields
      value={draft}
      onChange={(next) => {
        setDraft(next);
        applyValue({ ...item, value: next });
      }}
    />
  );
}

export function getMrcogFilterOperators(): GridFilterOperator[] {
  return [
    {
      label: "matches",
      value: "rollup",
      getApplyFilterFn: () => null,
      InputComponent: MrcogFilterInput,
    },
  ];
}
